<template>
	<Menu
		ref="menuRef"
		as="div"
		class="relative inline-block text-left"
	>
		<MenuButton
			:disabled="isMenuDisabled"
			class="h-full"
		>
			<EllipsisVerticalIcon
				class="ml-2 h-7 w-7"
				:class="{ 'cursor-not-allowed text-slate-400': isMenuDisabled }"
			/>
		</MenuButton>
		<transition
			enter-active-class="transition duration-100 ease-out"
			enter-from-class="transform scale-95 opacity-0"
			enter-to-class="transform scale-100 opacity-100"
			leave-active-class="transition duration-75 ease-in"
			leave-from-class="transform scale-100 opacity-100"
			leave-to-class="transform scale-95 opacity-0"
		>
			<MenuItems
				@click.prevent=""
				as="section"
				class="text-secondary-800 absolute right-0 z-2 flex w-56 flex-col rounded-md border border-slate-100 bg-white text-xs font-light shadow-md"
			>
				<MenuItem
					:disabled="!hasAnyPolicyPacket"
					as="component"
					class="px-3 pt-2 pb-1"
					:class="
						hasAnyPolicyPacket
							? 'hover:cursor-pointer hover:bg-slate-100'
							: 'pointer-events-none cursor-none text-slate-400'
					"
					@click.prevent="printPackets"
					>{{ t('home.print.printPolicyPacket') }}
				</MenuItem>
				<MenuItem
					:disabled="!hasExistingPlans"
					as="component"
					class="px-3 pt-1 pb-2"
					:class="
						hasExistingPlans
							? 'hover:cursor-pointer hover:bg-slate-100'
							: 'pointer-events-none cursor-none text-slate-400'
					"
					@click.prevent="printMemorialGuide"
					>{{ t('home.print.printMemorialGuide') }}
				</MenuItem>
				<MenuItem
					:disabled="!hasExistingPlans"
					as="component"
					class="px-3 pt-1 pb-2"
					:class="
						hasExistingPlans
							? 'hover:cursor-pointer hover:bg-slate-100'
							: 'pointer-events-none cursor-none text-slate-400'
					"
					@click="checkSubmissionStatus"
					>{{ t('home.submissions.checkStatusButton') }}
				</MenuItem>
			</MenuItems>
		</transition>
	</Menu>
	<SubmissionStatusModal
		v-if="hasExistingPlans"
		:plans="appointmentPlans"
		v-model:open="openStatusModal"
	/>
</template>

<script setup lang="ts">
import type { CounselorAppointment } from '@/apiTypes'
import { Session } from '@/classes/Session'
import { useFoxitInstance } from '@/components/documents/Foxit/composables/FoxitInstance'
import { useFoxitManager } from '@/components/documents/Foxit/composables/FoxitManager'
import SubmissionStatusModal from '@/components/home/SubmissionStatuses/SubmissionStatusModal.vue'
import {
	getPolicyPacketDocuments,
	shouldDocumentBeIncluded,
} from '@/components/home/composables/getPolicyPacketDocuments'
import { type PlanVersion, usePlanVersionsV2 } from '@/mixins/planVersionsV2'
import { useDocumentStore } from '@/stores/document'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import { EllipsisVerticalIcon } from '@heroicons/vue/24/solid'
import { useIntersectionObserver } from '@vueuse/core'
import { computed, type PropType, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

const props = defineProps({
	appointment: {
		type: Object as PropType<CounselorAppointment>,
		required: false,
	},
	session: {
		type: Object as PropType<Session>,
		required: false,
	},
})

const { t } = useI18n()

const menuRef = ref()
const loaded = ref(false)
const latestPlanVersionByPerson = ref<Record<string, PlanVersion>>({})

const { prepareUiandViewer, getPdfViewer } = useFoxitInstance()
const foxitManager = useFoxitManager()
const versionHelper = usePlanVersionsV2()
const documentStore = useDocumentStore()
const router = useRouter()

const openStatusModal = ref<boolean | undefined>(false)

const { gatherDocumentsForAppointment } = getPolicyPacketDocuments()

// Only load plan versions when the print menu enters the viewport to avoid
// IndexedDB lookups for appointments that aren't currently visible.
const { stop } = useIntersectionObserver(
	menuRef,
	async ([{ isIntersecting }]) => {
		if (!isIntersecting || loaded.value) return

		latestPlanVersionByPerson.value =
			await versionHelper.getLatestPlanVersionsFromAppointment(
				props.session,
				props.appointment
			)
		loaded.value = true

		// Plan versions only need to be loaded once, so stop observing.
		stop()
	}
)

const isMenuDisabled = computed(() => {
	return loaded.value && !hasAnyPolicyPacket.value && !hasExistingPlans.value
})

const appointmentPlans = computed(() => {
	return Object.values(latestPlanVersionByPerson.value).map((planVersion) => {
		return {
			id: planVersion.id,
			name: planVersion.fullName,
		}
	})
})

const checkSubmissionStatus = async () => {
	// open the modal ( if there's plans )
	openStatusModal.value = true
}

// used to determine whether an appointment has anything to print/download
const hasAnyPolicyPacket = computed(() => {
	return Object.values(latestPlanVersionByPerson.value).some(
		hasCompletedDocuments
	)
})

// memorial guide print or status check can only happen
// when an appointment has existing plans
const hasExistingPlans = computed(() => {
	return !!Object.values(latestPlanVersionByPerson.value).length
})

// used to determine whether a plan has completed documents
const hasCompletedDocuments = (plan: PlanVersion): boolean => {
	if (!plan.documents || !plan.documents.length) {
		return false
	}

	return plan.documents.some((document) =>
		shouldDocumentBeIncluded(document, plan)
	)
}

const printPackets = async () => {
	// flatten the array of documents
	const flattenedArray = (
		await gatherDocumentsForAppointment(
			Object.values(latestPlanVersionByPerson.value)
		)
	).flat()

	// Prepare PDF viewer which is required for merging documents if none exists
	if (!getPdfViewer()) {
		await prepareUiandViewer(`print-home-dashboard`)
	}

	// merge them using foxit and print
	const buffer = await foxitManager.mergeDocs(flattenedArray)
	if (!buffer) {
		return
	}
	const file = new Blob([buffer], { type: 'application/pdf' })
	await printPdfPreview(file)
}

const printPdfPreview = async (file: Blob) => {
	const url = URL.createObjectURL(file)

	const routeData = router.resolve({
		path: '/print',
		query: { file: url },
	})

	window.open(routeData.href, '_blank')
}

/**
 * Memorial Guide can either come from a walk-in appointment or regular one
 * If a session is available, we prioritize the session and pass print page the sessionId
 * If there is no session, we look up plans by the lead id, and pass print page the appointmentId
 * */
const printMemorialGuide = async () => {
	// guide user to print memorial guide route
	const routeName = props.session
		? 'print-session-mem-guide'
		: 'print-appointment-mem-guide'
	const identifier = props.session
		? { sessionId: props.session.id }
		: { appointmentId: props.appointment?.id }

	const appointmentPrintRoute = router.resolve({
		name: routeName,
		params: identifier,
	})
	window.open(appointmentPrintRoute.href, '_blank')
}
</script>
